"use client";
import React, { useState, useEffect, useCallback } from 'react';
import { 
    Popover, 
    Box, 
    Typography, 
    Button, 
    TextField, 
    RadioGroup, 
    FormControlLabel, 
    Radio, 
    Divider,
    useTheme,
    useMediaQuery,
    Rating,
    Drawer,
    Collapse,
    IconButton
} from '@mui/material';
import {
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { sortOptions } from './sortOptions';
import { useDeals } from 'app/providers/deals-provider';

const FilterSection = ({ title, children, sectionKey, showContent = true, expandedSections, toggleSection  }) => (
    <Box sx={{ mb: 2.5 }}>
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 0.5,
            cursor: 'pointer'
        }}
        onClick={() => toggleSection(sectionKey)}
        >
            <Typography 
                variant="subtitle1" 
                sx={{ 
                    fontWeight: 600, 
                    color: 'var(--theme-dark, #2B4D59)',
                    fontSize: '14px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px'
                }}
            >
                {title}
            </Typography>
            <IconButton
                size="small"
                sx={{ 
                    color: 'var(--theme-dark, #2B4D59)',
                    p: 0.5 
                }}
            >
                {expandedSections[sectionKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
        </Box>
        <Collapse in={expandedSections[sectionKey] && showContent}>
            <Box sx={{ pt: 1 }}>
                {children}
            </Box>
        </Collapse>
    </Box>
);

const FiltersPopover = ({ 
    open, 
    anchorEl,
    onClose,
    hideSort = false
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { 
        filters, 
        setFilters, 
        collectionCategories, 
        selectedCollectionCategory, 
        setSelectedCollectionCategory,
        collection
    } = useDeals();

    // Filter states
    const [ratingStars, setRatingStars] = useState(filters.ratingStars || '');
    const [ratingCount, setRatingCount] = useState(filters.ratingCount || '');
    const [maxPrice, setMaxPrice] = useState(filters.price || '');
    const [discount, setDiscount] = useState(filters.discount || '');
    const [sortValue, setSortValue] = useState(filters.sort || '');
    const [localSelectedSubCategory, setLocalSelectedSubCategory] = useState(selectedCollectionCategory);

    // Collapsible sections state
    const [expandedSections, setExpandedSections] = useState({
        sort: true,
        subcategories: true,
        maxPrice: true,
        discount: true,
        customerRating: true,
        ratingCount: true
    });

    // Prevent scroll on number input
    useEffect(() => {
        const handleWheel = (e) => {
            if (document.activeElement.type === 'text') {
                e.preventDefault();
            }
        };
        
        window.addEventListener('wheel', handleWheel, { passive: false });
        return () => window.removeEventListener('wheel', handleWheel);
    }, []);

    const handleReset = () => {
        // Reset all filter states
        setRatingStars('');
        setRatingCount('');
        setMaxPrice('');
        setDiscount('');
        setSortValue('');

        // Reset subcategory selection
        setLocalSelectedSubCategory('');
        setSelectedCollectionCategory('');

        // Apply empty filters to trigger refresh
        const emptyFilters = {
            ratingStars: '',
            ratingCount: '',
            price: '',
            discount: '',
            sort: ''
        };
        
        // Apply empty filters to parent component
        setFilters(emptyFilters);
        onClose();
    };

    // Reset local state when filters changes
    useEffect(() => {
        setRatingStars(filters.ratingStars || '');
        setRatingCount(filters.ratingCount || '');
        setMaxPrice(filters.price || '');
        setDiscount(filters.discount || '');
    }, [filters]);

    const handleApply = () => {
        const filters = {
            ratingStars: ratingStars || '',
            ratingCount: ratingCount || '',
            price: maxPrice || '',
            discount: discount || '',
            sort: sortValue
        };
        setFilters(filters);
        if (localSelectedSubCategory) {
            setSelectedCollectionCategory(localSelectedSubCategory);
        }
        onClose();
    };

    const toggleSection = (sectionName) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionName]: !prev[sectionName]
        }));
    };

    const PriceInput = useCallback(({ value, onChange, placeholder }) => (
        <TextField
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.target.select()}
            inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                min: '1',
                style: { 
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield'
                }
            }}
            type="number"
            sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '14px',
                    bgcolor: '#fff',
                    '& fieldset': {
                        borderColor: 'rgba(224, 224, 224, 0.8)',
                    },
                    '&:hover fieldset': {
                        borderColor: 'var(--theme-dark, #2B4D59)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'var(--theme-dark, #2B4D59)',
                    },
                    '& input': {
                        padding: '10px 12px',
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                            '-webkit-appearance': 'none',
                            margin: 0
                        }
                    }
                },
                '& .MuiInputBase-input::placeholder': {
                    color: '#A0AEC0',
                    opacity: 1
                }
            }}
        />
    ), []);

    const hasSubcategories = collection?.isCollection && collectionCategories && collectionCategories.length > 0;

    const filterContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box 
                sx={{ 
                    flex: 1, 
                    overflowY: 'auto', 
                    maxHeight: isMobile ? undefined : '60vh',
                    p: 3,
                    pt: 2.5,
                    overscrollBehavior: 'contain',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'rgba(0,0,0,0.04)',
                        borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(43, 77, 89, 0.3)',
                        borderRadius: '10px',
                        '&:hover': {
                            background: 'rgba(43, 77, 89, 0.5)',
                        }
                    }
                }}
            >
                {/* Sort Options (Mobile Only) */}
                {isMobile && !hideSort && (
                    <FilterSection title="Sort By" sectionKey="sort" expandedSections={expandedSections} toggleSection={toggleSection}>
                        <RadioGroup
                            value={sortValue}
                            onChange={(e) => setSortValue(e.target.value)}
                            sx={{ ml: 0.5 }}
                        >
                            {sortOptions.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={
                                        <Radio 
                                            size="small"
                                            sx={{
                                                color: 'rgba(43, 77, 89, 0.4)',
                                                '&.Mui-checked': {
                                                    color: 'var(--theme-dark, #2B4D59)',
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ 
                                            fontSize: '14px', 
                                            color: '#4A5568',
                                            fontWeight: 500
                                        }}>
                                            {option.label}
                                        </Typography>
                                    }
                                />
                            ))}
                        </RadioGroup>
                        <Divider sx={{ my: 2 }} />
                    </FilterSection>
                )}

                {/* Subcategories Filter (Mobile Only) */}
                {isMobile && hasSubcategories && (
                    <FilterSection title="Subcategories" sectionKey="subcategories" expandedSections={expandedSections} toggleSection={toggleSection}>
                        <RadioGroup
                            value={localSelectedSubCategory?.value}
                            onChange={(e) => {
                                const category = collectionCategories.find(cat => cat.value == e.target.value);    
                                setLocalSelectedSubCategory(category);
                            }}
                            sx={{ ml: 0.5 }}
                        >
                            {collectionCategories.map((category) => (
                                <FormControlLabel
                                    key={category.value}
                                    value={category.value}
                                    control={
                                        <Radio 
                                            size="small"
                                            sx={{
                                                color: 'rgba(43, 77, 89, 0.4)',
                                                '&.Mui-checked': {
                                                    color: 'var(--theme-dark, #2B4D59)',
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ 
                                            fontSize: '14px', 
                                            color: '#4A5568',
                                            fontWeight: 500
                                        }}>
                                            {category.label}
                                        </Typography>
                                    }
                                />
                            ))}
                        </RadioGroup>
                        <Divider sx={{ my: 2 }} />
                    </FilterSection>
                )}

                {/* Price Range Filter */}
                <FilterSection title="Maximum Price" sectionKey="maxPrice" expandedSections={expandedSections} toggleSection={toggleSection}>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            alignItems: 'center', 
                            px: 0.5,
                            position: 'relative',
                            zIndex: 1
                        }}
                    >
                        <PriceInput
                            value={maxPrice}
                            onChange={(e)=>setMaxPrice(e.target.value)}
                            placeholder="Max Price"
                        />
                    </Box>
                </FilterSection>

                {/* Discount Filter */}
                <FilterSection title="Discount" sectionKey="discount" expandedSections={expandedSections} toggleSection={toggleSection}>
                    <RadioGroup
                        value={discount}
                        onChange={(e) => setDiscount(e.target.value)}
                        sx={{ ml: 0.5 }}
                    >
                        {['60', '50', '25', '10'].map((discountRange) => (
                            <FormControlLabel
                                key={discountRange}
                                value={discountRange}
                                control={
                                    <Radio 
                                        size="small"
                                        sx={{
                                            color: 'rgba(43, 77, 89, 0.4)',
                                            '&.Mui-checked': {
                                                color: 'var(--theme-dark, #2B4D59)',
                                            }
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ 
                                        fontSize: '14px', 
                                        color: '#4A5568',
                                        fontWeight: 500
                                    }}>
                                        {discountRange}% off or more
                                    </Typography>
                                }
                            />
                        ))}
                    </RadioGroup>
                </FilterSection>

                {/* Rating Stars Filter */}
                <FilterSection title="Customer Ratings" sectionKey="customerRating" expandedSections={expandedSections} toggleSection={toggleSection}>
                    <RadioGroup
                        value={ratingStars}
                        onChange={(e) => setRatingStars(e.target.value)}
                        sx={{ ml: 0.5 }}
                    >
                        {['5', '4', '3', '2'].map((rating) => (
                            <FormControlLabel
                                key={rating}
                                value={rating}
                                control={
                                    <Radio 
                                        size="small"
                                        sx={{
                                            color: 'rgba(43, 77, 89, 0.4)',
                                            '&.Mui-checked': {
                                                color: 'var(--theme-dark, #2B4D59)',
                                            }
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center',
                                        gap: 1
                                    }}>
                                        <Rating 
                                            value={parseInt(rating)} 
                                            readOnly 
                                            max={5}
                                            sx={{
                                                color: 'var(--theme-dark, #2B4D59)',
                                                '& .MuiRating-iconFilled': {
                                                    color: 'var(--theme-dark, #2B4D59)',
                                                },
                                                '& .MuiRating-iconEmpty': {
                                                    color: 'rgba(43, 77, 89, 0.2)',
                                                }
                                            }}
                                        />
                                        <Typography sx={{ 
                                            fontSize: '14px', 
                                            color: '#4A5568',
                                            fontWeight: 500
                                        }}>
                                            {rating === '5' ? '':'& up'}
                                        </Typography>
                                    </Box>
                                }
                            />
                        ))}
                    </RadioGroup>
                </FilterSection>

                {/* Rating Count Filter */}
                <FilterSection title="Rating Count" sectionKey="ratingCount" expandedSections={expandedSections} toggleSection={toggleSection}>
                    <RadioGroup
                        value={ratingCount}
                        onChange={(e) => setRatingCount(e.target.value)}
                        sx={{ ml: 0.5 }}
                    >
                        {['100+', '500+', '1000+', '2500+', '5000+'].map((count) => (
                            <FormControlLabel
                                key={count}
                                value={count}
                                control={
                                    <Radio 
                                        size="small"
                                        sx={{
                                            color: 'rgba(43, 77, 89, 0.4)',
                                            '&.Mui-checked': {
                                                color: 'var(--theme-dark, #2B4D59)',
                                            }
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ 
                                        fontSize: '14px', 
                                        color: '#4A5568',
                                        fontWeight: 500
                                    }}>
                                        {count}
                                    </Typography>
                                }
                            />
                        ))}
                    </RadioGroup>
                </FilterSection>
            </Box>

            {/* Footer */}
            <Box sx={{ 
                p: 3,
                pt: 2, 
                borderTop: '1px solid rgba(224, 224, 224, 0.6)',
                display: 'flex',
                gap: 2,
                background: 'linear-gradient(135deg, #FAFBFC 0%, #F5F6F8 100%)',
            }}>
                <Button
                    variant="outlined"
                    onClick={handleReset}
                    sx={{
                        flex: 1,
                        borderRadius: '8px',
                        borderColor: 'rgba(43, 77, 89, 0.3)',
                        color: 'var(--theme-dark, #2B4D59)',
                        fontSize: '14px',
                        fontWeight: 600,
                        py: 1.2,
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: 'var(--theme-dark, #2B4D59)',
                            bgcolor: 'rgba(43, 77, 89, 0.06)'
                        }
                    }}
                >
                    Reset
                </Button>
                <Button
                    variant="contained"
                    onClick={handleApply}
                    sx={{
                        flex: 1,
                        borderRadius: '8px',
                        bgcolor: 'var(--theme-dark, #2B4D59)',
                        fontSize: '14px',
                        fontWeight: 600,
                        py: 1.2,
                        textTransform: 'none',
                        '&:hover': {
                            bgcolor: 'rgba(43, 77, 89, 0.9)'
                        }
                    }}
                >
                    Apply Filters
                </Button>
            </Box>
        </Box>
    );

    if (isMobile) {
        return (
            <Drawer
                anchor="bottom"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        height: '70vh',
                        borderTopLeftRadius: '16px',
                        borderTopRightRadius: '16px',
                        bgcolor: '#FAFBFC',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08)',
                        border: '1px solid rgba(224, 224, 224, 0.8)',
                        backdropFilter: 'blur(8px)',
                        overflow: 'hidden',
                    }
                }}
            >
                {filterContent}
            </Drawer>
        );
    }

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            sx={{
                '& .MuiPopover-paper': {
                    width: '350px !important',
                    maxWidth: '350px !important'
                }
            }}
            PaperProps={{
                elevation: 8,
                sx: {
                    width: '250px !important',
                    maxWidth: '250px !important',
                    maxHeight: '85vh',
                    bgcolor: '#FAFBFC',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08)',
                    borderRadius: '12px',
                    border: '1px solid rgba(224, 224, 224, 0.8)',
                    backdropFilter: 'blur(8px)',
                    overflow: 'hidden',
                    '& *': {
                        overscrollBehavior: 'contain'
                    }
                }
            }}
        >
            {filterContent}
        </Popover>
    );
};

export default FiltersPopover;